/**
 * MarketingActivitiesColumn 组件
 * 职责: 管理周策略中的营销活动
 *
 * 重构后结构:
 * - constants.ts: 常量定义
 * - utils.ts: 工具函数和成本计算
 * - AddActivityForm.tsx: 添加活动表单
 * - ActivityItem.tsx: 活动列表项
 */

import React, { useState } from 'react';
import { Button, Popover, Typography, Divider } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { MarketingActivity, MarketingActivityType, VineMarketplace } from '../../ProfitScenario/types/marketingActivities';
import { calculateActivityCost } from './MarketingActivities/utils';
import { AddActivityForm } from './MarketingActivities/AddActivityForm';
import { ActivityItem } from './MarketingActivities/ActivityItem';

const { Text } = Typography;

interface MarketingActivitiesColumnProps {
    activities?: MarketingActivity[];
    landedCost: number;
    fbaFee: number;
    shippingCost: number;
    onUpdate: (activities: MarketingActivity[]) => void;
}

interface NewActivityState {
    type: MarketingActivityType;
    units: number;
    marketplace: VineMarketplace;
    videoCostPerUnit: number;
    slotFee: number;
}

export const MarketingActivitiesColumn: React.FC<MarketingActivitiesColumnProps> = ({
    activities = [],
    landedCost,
    fbaFee,
    shippingCost,
    onUpdate,
}) => {
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [newActivity, setNewActivity] = useState<NewActivityState>({
        type: 'vine',
        units: 1,
        marketplace: 'US',
        videoCostPerUnit: 200,
        slotFee: 0,
    });

    const handleAddActivity = () => {
        const activity = calculateActivityCost({
            ...newActivity,
            landedCost,
            fbaFee,
            shippingCost,
        });
        onUpdate([...activities, activity]);
        setPopoverVisible(false);
    };

    const handleDeleteActivity = (id: string) => {
        onUpdate(activities.filter(a => a.id !== id));
    };

    const totalCost = activities.reduce((sum, a) => sum + a.totalCost, 0);

    return (
        <div style={{ fontSize: 13 }}>
            {activities.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                    {activities.map(activity => (
                        <ActivityItem key={activity.id} activity={activity} onDelete={handleDeleteActivity} />
                    ))}
                    <Divider style={{ margin: '8px 0' }} />
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong style={{ fontSize: 13 }}>
                    {totalCost > 0 ? `总计: $${totalCost.toFixed(0)}` : '无活动'}
                </Text>
                <Popover
                    content={
                        <AddActivityForm
                            newActivity={newActivity}
                            onActivityChange={setNewActivity}
                            onAdd={handleAddActivity}
                        />
                    }
                    trigger="click"
                    open={popoverVisible}
                    onOpenChange={setPopoverVisible}
                    placement="bottomRight"
                >
                    <Button size="small" icon={<PlusOutlined />}>
                        添加
                    </Button>
                </Popover>
            </div>
        </div>
    );
};

export default MarketingActivitiesColumn;
